import { color, scaleSequential, ScaleTime } from "d3"
import { ColorSpectrum, getSpectrumInterpolator } from "../../../../Types/ColorSpectrum"
import { ModalityPage } from "../../../../Data/ModalityPage"
import { CompositeTimeSeriesData } from "../../../../Data/TimeSeriesData"
import { PageRectangle, SpectrogramTraceConfig } from "../../../../Types/Trace"
import { TimeSeriesPageManager } from "../../../../Data/TimeSeriesPageManager"
import { D3Trace } from "./D3Trace"
import { ReactCallbacks } from "../../../../Types/ReactCallbacks"
import { TraceRenderStrategy, TraceRendererOptions } from "./RenderStrategy"

export class SpectrogramRenderStrategy extends TraceRenderStrategy {
	private colorScale = scaleSequential(getSpectrumInterpolator(ColorSpectrum.INFERNO))
	private rendererStub = {
		context: (_ctx?: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null) => undefined,
	}

	private colorLUT = new Uint8Array(256 * 3)

	private pageManager: TimeSeriesPageManager<ModalityPage>
	private config: SpectrogramTraceConfig
	private d3Trace: D3Trace

	constructor(pageManager: TimeSeriesPageManager<ModalityPage>, d3Trace: D3Trace, config: SpectrogramTraceConfig) {
		super()
		this.pageManager = pageManager
		this.config = config
		this.d3Trace = d3Trace

		this.colorScale = scaleSequential(getSpectrumInterpolator(this.config.colorSpectrum)).domain([this.config.lowerBound, this.config.upperBound])
		this.buildColorLUT()
	}

	private buildColorLUT(): void {
		const { lowerBound, upperBound } = this.config
		const range = upperBound - lowerBound

		for (let i = 0; i < 256; i++) {
			const normalizedValue = lowerBound + (i / 255) * range
			const hexColor = this.colorScale(normalizedValue)
			const rgb = this.parseColor(hexColor)
			this.colorLUT[i * 3] = rgb.r
			this.colorLUT[i * 3 + 1] = rgb.g
			this.colorLUT[i * 3 + 2] = rgb.b
		}
	}

	private parseColor(colorString: string): { r: number; g: number; b: number } {
		const parsed = color(colorString)
		if (parsed) {
			const rgb = parsed.rgb()
			return { r: rgb.r, g: rgb.g, b: rgb.b }
		}
		return { r: 255, g: 255, b: 255 }
	}

	private getColorFromLUT(value: number | undefined): [number, number, number] | null {
		if (value === undefined || Number.isNaN(value)) {
			return null
		}

		const { lowerBound, upperBound } = this.config
		const normalized = (value - lowerBound) / (upperBound - lowerBound)

		if (normalized > 1 || normalized < 0) {
			const hexColor = this.colorScale(value)
			const rgb = this.parseColor(hexColor)
			return [rgb.r, rgb.g, rgb.b]
		}

		const lutIndex = Math.round(normalized * 255) * 3
		return [this.colorLUT[lutIndex], this.colorLUT[lutIndex + 1], this.colorLUT[lutIndex + 2]]
	}

	public renderPage(
		page: ModalityPage,
		reactCallbacks: ReactCallbacks<any>,
		offscreenXScale: ScaleTime<any, any, any>,
		offscreenCanvas: OffscreenCanvas,
		context: CanvasRenderingContext2D,
		offscreenContext: OffscreenCanvasRenderingContext2D,
		pageRectangle: PageRectangle,
		unloadedRectangle: PageRectangle | null,
	): void {
		const { x, y, width, height } = pageRectangle
		const dataObjectId = reactCallbacks.dataSourceMap.get(this.config.dataSource) ?? Infinity
		const traceData = page.data.get(dataObjectId)?.get(this.config.dataKey)

		if (!traceData) {
			context.fillStyle = this.getUnloadedColor()
			context.fillRect(x, y, width, height)
			return
		}

		const compositeData = traceData as unknown as CompositeTimeSeriesData

		const canvasWidth = Math.max(1, Math.round(width))
		const canvasHeight = Math.max(1, Math.round(height))

		offscreenCanvas.width = canvasWidth
		offscreenCanvas.height = canvasHeight
		offscreenContext.clearRect(0, 0, canvasWidth, canvasHeight)
		this.offscreenFillInUnloadedRegion(offscreenContext, unloadedRectangle)

		const hasValidData = Array.isArray(compositeData.data) && compositeData.data.length > 0 && compositeData.times.length > 0

		if (!hasValidData) {
			offscreenContext.fillStyle = this.getUnloadedColor()
			offscreenContext.fillRect(0, 0, canvasWidth, canvasHeight)
			const bitmap = offscreenCanvas.transferToImageBitmap()
			page.updateRenderCache(dataObjectId, this.getRenderCacheKey(), { bitmap, dirty: false, edges: [] })
			context.drawImage(bitmap, Math.round(x), Math.round(y))
			return
		}

		offscreenXScale.domain([page.startTime, page.endTime]).range([0, canvasWidth])
		const samplingPeriod = page.timingInformation?.get(dataObjectId)?.get(this.config.dataKey)?.samplingPeriod

		this.drawSpectrogram(offscreenContext, compositeData, canvasWidth, canvasHeight, offscreenXScale, samplingPeriod)
		this.offscreenFillInUnloadedRegion(offscreenContext, unloadedRectangle)

		const bitmap = offscreenCanvas.transferToImageBitmap()
		page.updateRenderCache(dataObjectId, this.getRenderCacheKey(), { bitmap, dirty: false, edges: [] })
		context.drawImage(bitmap, Math.round(x), Math.round(y))
	}

	getRenderCacheKey(): string {
		const { graphId, dataKey, dataSource, colorSpectrum, lowerBound, upperBound, displayScale, id } = this.config
		return [graphId, id, dataKey, dataSource, colorSpectrum, lowerBound, upperBound, displayScale].join("-")
	}

	public updateConfig(traceConfig: SpectrogramTraceConfig): void {
		this.config = traceConfig
		this.colorScale = scaleSequential(getSpectrumInterpolator(this.config.colorSpectrum)).domain([this.config.lowerBound, this.config.upperBound])
		this.buildColorLUT()
	}

	public getOffscreenRenderer = (_options?: TraceRendererOptions) => this.rendererStub

	public getDirectRenderer = (_options?: TraceRendererOptions) => this.rendererStub

	public render(): void {
		this.pageManager.getPagesInView().forEach(page => this.d3Trace.renderPage(page))
	}

	private drawSpectrogram(
		context: OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D,
		compositeData: CompositeTimeSeriesData,
		width: number,
		height: number,
		xScale: ScaleTime<any, any, any>,
		samplingPeriod?: number | null
	) {
		const timePoints = compositeData.times.length
		const frequencyLabels = this.config.frequencyLabels ?? []

		if (timePoints === 0 || frequencyLabels.length === 0) {
			return
		}

		const shouldFillSingleTimePointPage = timePoints === 1

		if (!samplingPeriod && timePoints > 1) {
			samplingPeriod = compositeData.times[1] - compositeData.times[0]
		}

		if (!shouldFillSingleTimePointPage && !samplingPeriod) {
			return
		}

		const yScale = this.config.yScale
		const yBandHeight = Math.max(yScale.bandwidth(), 1)
		const cellHeight = Math.ceil(yBandHeight)

		const startTime = xScale.domain()[0]
		const barWidth = shouldFillSingleTimePointPage ? width : Math.abs(xScale(new Date(startTime.getTime() + samplingPeriod!)) - xScale(startTime))
		const cellWidth = Math.max(Math.ceil(barWidth), 1)

		const seriesCount = Math.min(frequencyLabels.length, compositeData.data.length)

		// Get the y-offset to convert from graph coordinates to canvas-local coordinates
		const yRangeStart = yScale.range()[0]

		const yPositions: (number | undefined)[] = frequencyLabels.map(label => {
			const y = yScale(label)
			return y !== undefined ? Math.floor(y - yRangeStart) : undefined
		})

		const imageData = context.createImageData(width, height)
		const pixels = imageData.data

		const backgroundRgb = this.parseColor(this.getUnloadedColor())
		const WHITE_R = 255
		const WHITE_G = 255
		const WHITE_B = 255

		for (let i = 0; i < pixels.length; i += 4) {
			pixels[i] = backgroundRgb.r
			pixels[i + 1] = backgroundRgb.g
			pixels[i + 2] = backgroundRgb.b
			pixels[i + 3] = 255
		}

		for (let timeIndex = 0; timeIndex < timePoints; timeIndex++) {
			const time = compositeData.times[timeIndex]
			const xPosition = xScale(time)

			if (xPosition === undefined) {
				continue
			}

			let xMin = 0
			let xMax = width

			if (!shouldFillSingleTimePointPage) {
				const xStart = Math.floor(xPosition - cellWidth / 2)

				if (xStart + cellWidth <= 0 || xStart >= width) {
					continue
				}

				xMin = Math.max(0, xStart)
				xMax = Math.min(width, xStart + cellWidth)

				if (timeIndex === 0) {
					const pageStartTime = (xScale.domain()[0] as Date).getTime()
					const timeDiffFromStart = time - pageStartTime
					if (timeDiffFromStart < samplingPeriod!) {
						xMin = 0
					}
				}
				if (timeIndex === timePoints - 1) {
					const pageEndTime = (xScale.domain()[1] as Date).getTime()
					const timeDiffFromEnd = pageEndTime - time
					if (timeDiffFromEnd < samplingPeriod!) {
						xMax = width
					}
				}
			}

			for (let freqIndex = 0; freqIndex < seriesCount; freqIndex++) {
				const row = compositeData.data[freqIndex]
				if (!row) continue

				const yStart = yPositions[freqIndex]
				if (yStart === undefined) continue

				if (yStart + cellHeight <= 0 || yStart >= height) {
					continue
				}

				const value = row[timeIndex]
				const rgb = this.getColorFromLUT(value)

				const r = rgb ? rgb[0] : WHITE_R
				const g = rgb ? rgb[1] : WHITE_G
				const b = rgb ? rgb[2] : WHITE_B

				const yMin = Math.max(0, yStart)
				const yMax = Math.min(height, yStart + cellHeight)

				for (let py = yMin; py < yMax; py++) {
					const rowOffset = py * width * 4
					for (let px = xMin; px < xMax; px++) {
						const idx = rowOffset + px * 4
						pixels[idx] = r
						pixels[idx + 1] = g
						pixels[idx + 2] = b
						pixels[idx + 3] = 255
					}
				}
			}
		}

		context.putImageData(imageData, 0, 0)
	}
}
